/**
 * React Query Hook: Merge Branch
 *
 * Merges a branch into main. Returns a conflict if the file changed on main
 * since the branch was created. Retry with `resolvedConflict` to force-resolve.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { MERGE_BRANCH_MUTATION } from '@/services/metadata_v1/queries';
import type {
  BranchConflict,
  MergeBranchResponse,
  MergeBranchResult,
  MergeBranchVariables,
  MetadataBranch,
  ResolvedConflictInput,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

async function mergeBranch(
  variables: MergeBranchVariables
): Promise<MergeBranchResult> {
  const response = await graphqlRequest<MergeBranchResponse>(
    MERGE_BRANCH_MUTATION,
    variables
  );

  if (!response?.mergeBranch) {
    throw new Error('Merge failed. Please try again.');
  }

  return response.mergeBranch;
}

export interface UseMergeBranchInput {
  branchId: string;
  /** Provide when retrying after a conflict is detected */
  resolvedConflict?: ResolvedConflictInput;
}

export interface UseMergeBranchOptions {
  onSuccess?: (data: MetadataBranch) => void;
  /** Called when the merge is blocked by a conflict */
  onConflict?: (conflict: BranchConflict) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to merge a branch into main.
 *
 * Two-step flow:
 * 1. Call mutate({ branchId }) — may return a conflict
 * 2. If `onConflict` fires, show resolution UI and retry with `resolvedConflict`
 *
 * Invalidates `activeBranch`, `userBranches`, and `metadataItem` caches on success.
 */
export function useMergeBranch(
  options?: UseMergeBranchOptions
): UseMutationResult<MergeBranchResult, Error, UseMergeBranchInput> {
  const queryClient = useQueryClient();
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const organizationId = selectedOrg?.id;

  return useMutation({
    mutationFn: ({ branchId, resolvedConflict }: UseMergeBranchInput) =>
      mergeBranch({
        input: { branchId, resolvedConflict },
      }),
    onSuccess: (result) => {
      if (!result.success && result.conflict) {
        // Conflict — not an error, let the caller handle the resolution UI
        options?.onConflict?.(result.conflict);
        return;
      }

      if (result.mergedBranch) {
        const metadataItemId = undefined; // we don't have it here; invalidate broadly
        queryClient.invalidateQueries({ queryKey: ['activeBranch'] });
        queryClient.invalidateQueries({ queryKey: ['userBranches', organizationId] });
        // Invalidate file content so editor picks up the merged version
        queryClient.invalidateQueries({ queryKey: ['metadataItem', organizationId] });

        options?.onSuccess?.(result.mergedBranch);
      }
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
